import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { OrderTraceKey } from '@/console/api/types'
import { AuthStorage } from '@/utils/storage/AuthStorage'
import { hasConsoleAccess } from '@/utils/consoleEntry'
import { DispatcherTab } from '@/console/tabs/dispatcher/DispatcherTab'
import { FoptTab } from '@/console/tabs/fopt/FoptTab'
import { TraceTab } from '@/console/tabs/trace/TraceTab'
import { MassTab } from '@/console/tabs/mass/MassTab'
import { InboundTab } from '@/console/tabs/inb/InboundTab'
import { ErpTab } from '@/console/tabs/erp/ErpTab'
import { OutboundSettingTab } from '@/console/tabs/obs/OutboundSettingTab'
import { WarehouseTab } from '@/console/tabs/wh/WarehouseTab'
import { ConsoleForbidden } from './ConsoleForbidden'
import { SideNav } from './SideNav'
import { TabBar } from './TabBar'
import { TABS, TabKey, buildConsoleHash, parseTabFromHash } from './tabs'
import { ThemeBar } from './ThemeBar'
import { useScrollSpy } from './useScrollSpy'
import styles from './ConsoleShell.module.css'

/**
 * LOGISPRD-12506 — 콘솔 셸. 탭 4개 · 사이드 내비 · 스크롤 스파이.
 *
 * 범위 축소 이력과 각 탭이 빠진 근거는 `shell/tabs.ts` 상단 주석에 모아 뒀다.
 *
 * 헤더는 직접 고르지 않고 `header` prop 으로 받는다 — 셸이 어떤 라우터 안에 있는지 모르게 해
 * 테스트에서 stub 하나로 갈아끼울 수 있게 한다. 실제 주입은 `ConsolePage` 가 한다.
 *
 * ## `.moms-console` 래퍼
 *
 * 콘솔 전역 CSS(`styles/tokens.css`·`styles/base.css`)의 스코프 루트다. 어드민과 같은 문서에서
 * 렌더되므로 `:root`/`body`/`select` 같은 전역 선택자를 그대로 두면 어드민 화면 53개의 폼이 전부
 * 바뀐다. 테마 속성(`data-theme`)은 계속 `documentElement` 에 붙지만(`useTheme.ts`) 값을 읽는
 * 셀렉터가 `.moms-console` 후손으로 좁혀져 있어 색이 새지 않는다.
 *
 * 탭 상태는 hash 로 복원한다 — 새로고침해도 보던 탭이 유지된다. `history.replaceState` 로
 * 바꿔 히스토리를 더럽히지 않는다(뒤로가기 한 번에 탭이 여러 번 튀는 걸 막는다).
 *
 * ## 글로벌 헤더 — 핸드오프 문서(§5)를 뒤집는 결정
 *
 * BE 핸드오프 문서(LOGISPRD-12506) §5 는 콘솔에 `@musinsa/mwms-ds`·
 * `MainHeader`·`MAlert`·`MLoader` 를 쓰지 말라고 못박았다("외관을 공유하지 않는 것이 이 화면의
 * 요구사항이다"). 사용자가 목업 검수 후 이 규칙을 명시적으로 뒤집었다 — 콘솔도 MOMS 화면 중
 * 하나로 보이게, 어드민과 같은 글로벌 헤더(`MainHeader`)를 그대로 최상단에 얹는다.
 *
 * `MainHeader` 의 로고 박스 폭은 `SIDEBAR_OPENED_WIDTH(240)` 를 전제로 짜여 있다. 콘솔 사이드바는
 * 시안 값이 220px 라 그대로 두면 헤더 로고 경계와 20px 어긋나서, 헤더와의 시각적 정렬을 우선해
 * 콘솔 사이드바도 240px 로 맞춘다(`ConsoleShell.module.css` 의 `.app`).
 *
 * 네트워크 호출 0 원칙: `MainHeader` 는 마운트 시 메뉴를 조회하지 않는다 — 메뉴 조회(`callToGetMenuList`)는
 * 화주 전환 버튼을 눌렀을 때만 부르고, 콘솔은 `showShipperConvert={false}` 라 그 버튼이 없다.
 *
 * ## 권한 게이트 (검수 반영, LOGISPRD-12506)
 *
 * 콘솔은 개발자 전용 화면이다. `AuthStorage.user.get().auth` 를 읽어 `hasConsoleAccess`
 * (`@/utils/consoleEntry` — 헤더의 "온콜 콘솔 ↗" 링크 노출 조건과 같은 단일 출처)로 판별하고,
 * 통과하지 못하면 `SideNav`·`TabBar`·탭 본문 대신 `ConsoleForbidden` 을 렌더한다(리다이렉트가
 * 아니다 — 주소를 알게 된 사람이 지금 상황을 이해할 수 있어야 한다는 사용자 결정). 헤더는 이 분기
 * 밖에서 항상 렌더된다 — 막는 대상은 콘솔 "본문"뿐이다.
 *
 * 로그인하지 않은 사용자는 여기까지 오지 않는다 — 어드민 진입은 `AuthChecker`(App.tsx)가 로그인
 * 화면으로 돌린다. 그래서 이 화면이 뜬다는 건 "로그인은 됐는데 AUTH_CD 가 다르다"는 뜻이다.
 *
 * ⚠️ 이 게이트는 UX 다. 보안 경계가 아니다 — 실제 차단은 BE `@devOpsGate`(핸드오프 §2.3)이고, 그
 * 플래그(`devops.consoleAuthz.enable`)는 지금 dev·운영 모두 OFF 라서 이 화면을 우회해 API 를 직접
 * 호출하면 인증만 되면 누구나 통과한다.
 */
export const ConsoleShell = ({ header }: { header: ReactNode }): JSX.Element => {
  const [tab, setTab] = useState<TabKey>(() => parseTabFromHash(window.location.hash))
  const [traceTarget, setTraceTarget] = useState<OrderTraceKey | null>(null)
  // 탭별 제출 상태 전체가 아니라 busy 만 받는다. 응답 전에 탭이 바뀌어 패널이 언마운트되면 결과를
  // 못 보고 다시 보내게 된다 — 재발사 · 정산 재발행 · 재매핑은 비멱등이고, 백필 · 롤백은 되돌릴
  // 근거(previousSkuId · 처리분 요약)가 응답에만 있다.
  const [foptBusy, setFoptBusy] = useState(false)
  const [massBusy, setMassBusy] = useState(false)
  const [inboundBusy, setInboundBusy] = useState(false)
  const [erpBusy, setErpBusy] = useState(false)
  const [obsBusy, setObsBusy] = useState(false)
  const [whBusy, setWhBusy] = useState(false)
  const tabLocked = foptBusy || massBusy || inboundBusy || erpBusy || obsBusy || whBusy
  const authorized = useMemo(() => hasConsoleAccess(AuthStorage.user.get().auth), [])

  const sectionIds = useMemo(() => {
    const tabDef = TABS.find((t) => t.key === tab)
    return tabDef ? tabDef.sections.map((s) => s.id) : []
  }, [tab])

  // LOGISPRD-13139 — 스크롤 박스. 창 대신 이 박스를 스크롤 스파이와 탭 전환이 본다(base.css `.moms-console`).
  const bodyRef = useRef<HTMLDivElement>(null)
  const activeSection = useScrollSpy(sectionIds, bodyRef)

  // LOGISPRD-13139 — 창이 스크롤하지 않으므로, 진입 직후 아무 데도 누르지 않으면 PageDown·Space 가
  // 움직일 곳이 없다. 스크롤 박스에 포커스를 둬 키보드 스크롤이 바로 먹게 한다.
  useEffect(() => {
    bodyRef.current?.focus({ preventScroll: true })
  }, [])

  /**
   * 뒤로/앞으로가 탭 전환을 되돌리게 한다. 어드민 라우트에서는 주소창의 해시가 곧 화면 상태라
   * (`#console/fopt`), 사용자가 뒤로가기를 누르면 브라우저가 해시만 바꾸고 리렌더는 일어나지
   * 않는다 — `hashchange` 를 직접 받아 상태를 맞춘다. 별도 엔트리에서도 같은 동작이다.
   *
   * 탭이 잠긴 동안(제출 응답 대기)은 해시를 현재 탭으로 되돌리고 탭을 바꾸지 않는다. 탭바 잠금과
   * 같은 이유다 — 패널이 언마운트되면 응답과 되돌릴 근거를 잃는다.
   */
  useEffect(() => {
    const onHashChange = (): void => {
      if (tabLocked) {
        window.history.replaceState(null, '', buildConsoleHash(tab))
        return
      }
      setTab(parseTabFromHash(window.location.hash))
    }
    window.addEventListener('hashchange', onHashChange)
    return () => window.removeEventListener('hashchange', onHashChange)
  }, [tabLocked, tab])

  const changeTab = useCallback((next: TabKey): void => {
    setTraceTarget(null)
    setTab(next)
    if (bodyRef.current) bodyRef.current.scrollTop = 0
    window.history.replaceState(null, '', buildConsoleHash(next))
  }, [])

  /**
   * LOGISPRD-13035 — F-OPT 조회 행의 "Trace →". 대상은 메모리로만 넘긴다(주소에 싣지 않는다 — YAGNI).
   *
   * 4차 리뷰 — `useCallback` 으로 참조를 고정한다. 이 함수는 `FoptTab` 을 거쳐 `OrderSearchPanel` 의
   * `rows` `useMemo` 의존성(`onTrace`)에 들어간다. 매 렌더 새 함수면 셸이 리렌더될 때마다(스크롤
   * 스파이의 `activeSection`, `foptBusy`) "더 보기" 로 누적된 표가 통째로 다시 만들어진다.
   * 본문은 setter 와 `window`, ref 만 쓰므로 의존성이 없다.
   */
  const openTrace = useCallback((target: OrderTraceKey): void => {
    setTraceTarget(target)
    setTab('trace')
    if (bodyRef.current) bodyRef.current.scrollTop = 0
    window.history.replaceState(null, '', buildConsoleHash('trace'))
  }, [])

  const jumpToSection = (id: string): void => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  return (
    <div className="moms-console">
      {header}
      {/*
        `.moms-console-body` — 헤더를 뺀 본문 스코프. 폼 리셋처럼 어드민 공용 컴포넌트에 닿으면
        안 되는 규칙이 이 안에서만 걸린다(`styles/base.css` 상단 주석 참고). 헤더를 이 안에 넣으면
        환경 셀렉터(`SelectBoxApi`)의 글자가 패딩에 밀려 사라진다 — 실제로 그렇게 보였다.
      */}
      <div className="moms-console-body" ref={bodyRef} tabIndex={-1}>
        {!authorized ? (
          <ConsoleForbidden />
        ) : (
          <div className={styles.app}>
            <SideNav tab={tab} activeSection={activeSection} onJump={jumpToSection} />
            <div className={styles.col}>
              <ThemeBar />
              <header className={styles.top}>
                <TabBar tab={tab} onChange={changeTab} locked={tabLocked} />
              </header>
              <div className={styles.hrule} />
              <main className={styles.main}>
                {tab === 'disp' && <DispatcherTab />}
                {tab === 'fopt' && <FoptTab onTrace={openTrace} onBusyChange={setFoptBusy} />}
                {tab === 'trace' && <TraceTab initialTarget={traceTarget} />}
                {tab === 'mass' && <MassTab onBusyChange={setMassBusy} />}
                {tab === 'inb' && <InboundTab onBusyChange={setInboundBusy} />}
                {tab === 'erp' && <ErpTab onBusyChange={setErpBusy} />}
                {tab === 'obs' && <OutboundSettingTab onBusyChange={setObsBusy} />}
                {tab === 'wh' && <WarehouseTab onBusyChange={setWhBusy} />}
              </main>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
